import React from 'react';
import { supabase } from '@/integrations/supabase/client';

interface BannerProps {
  id: string;
  title: string;
  imageUrl: string;
  linkUrl?: string;
  bannerType: 'leaderboard' | 'medium_rectangle' | 'skyscraper';
  className?: string;
}

const Banner = ({ id, title, imageUrl, linkUrl, bannerType, className = '' }: BannerProps) => {
  const handleClick = async () => {
    // Track banner click
    try {
      await supabase.from('banner_clicks').insert({
        banner_id: id,
        ip_address: null, // Client-side, so we can't get IP
        user_agent: navigator.userAgent,
        referer: window.location.href
      });

      // Update click count directly
      const { data: currentBanner } = await supabase
        .from('banners')
        .select('click_count')
        .eq('id', id)
        .single();
      
      if (currentBanner) {
        await supabase
          .from('banners')
          .update({ click_count: currentBanner.click_count + 1 })
          .eq('id', id);
      }
    } catch (error) {
      console.error('Failed to track banner click:', error);
    }

    if (linkUrl) {
      window.open(linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getDimensions = () => {
    switch (bannerType) {
      case 'leaderboard':
        return 'w-full max-w-[728px] h-auto aspect-[728/90]';
      case 'medium_rectangle':
        return 'w-full max-w-[300px] h-auto aspect-[300/250]';
      case 'skyscraper':
        return 'w-[160px] h-[600px]';
      default:
        return 'w-full h-auto';
    }
  };

  return (
    <div className={`banner-${bannerType} ${className} ${bannerType === 'leaderboard' ? 'flex flex-col items-center w-full' : ''}`}>
      <div className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wide text-left w-full">
        Anzeige
      </div>
      <div
        className={`${getDimensions()} cursor-pointer transition-transform hover:scale-105 bg-background border border-border rounded-lg overflow-hidden shadow-sm ${bannerType === 'leaderboard' ? 'mx-auto' : ''}`}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick();
          }
        }}
        aria-label={`Banner: ${title}`}
      >
        <img
          src={imageUrl}
          alt={title}
          className={`w-full h-full ${bannerType === 'medium_rectangle' ? 'object-contain' : 'object-cover'}`}
          loading="lazy"
          decoding="async"
          fetchPriority="low"
        />
      </div>
    </div>
  );
};

export default Banner;